import getConfig from 'next/config';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import BooksMenuItemList from '../../../../components/books/model1/BooksMenuItemList';
import BooksNotes from '../../../../components/books/model1/BooksNotes';
import BooksSection from '../../../../components/books/model1/BooksSection';
import BooksCompleteAlert from '../../../../components/books/model1/forms/BooksCompleteAlert';
import BooksRequestAlert from '../../../../components/books/model1/forms/BooksRequestAlert';
import BooksCenteringUnderLineMessage from '../../../../components/books/model1/texts/BooksCenteringUnderLineMessage';
import {
  UserContextConsumer,
  UserContextState,
} from '../../../../contexts/UserContext';
import { CreateUserWithResvQuery } from '../../../../infrastructure/repositories/AuthRepository';
import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';
import {
  setMeToBookInfo,
  setPostable,
} from '../../../../redux/actions/merchants/resources/books';
import { Dispatch } from '../../../../redux/reducers/merchants/resources/books/inputs';
import * as gtag from '../../../../utils/gtag';
import Button, { ButtonSize } from '../../../common/buttons/Button';
import scrollToTop from '../../../hoc/scrollToTop';
import * as $ from './helper';
import InputCompletionCreateUser from './InputCompletionCreateUser';

const config = getConfig();
const publicRuntimeConfig = config ? config.publicRuntimeConfig : {};
const isProduction = publicRuntimeConfig.coubicEnv === 'production';

interface ElementProps {
  merchantResource: IBooks.MerchantResource;
  settings: IBooks.SettingCompletion;
  postable: boolean;
  publicId: string;
  withExpressFlow: boolean;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    handleSetMe: (merchantId: string, resourceId: string) => Promise<void>;
    handlePostable: (postable: boolean) => void;
  };
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

class InputCompletion extends React.Component<Props, { submitted: boolean }> {
  constructor(props: Props) {
    super(props);
    this.handleCreateUser = this.handleCreateUser.bind(this);
    this.state = {
      submitted: false,
    };
  }

  private async handleCreateUser(
    query: CreateUserWithResvQuery,
    userCtx: UserContextState,
  ) {
    const { inputs, actions } = this.props;
    actions.handlePostable(false);
    userCtx.auth
      .createUserWithResv(query)
      .then(async _ => {
        this.setState({ submitted: true });
        await actions.handleSetMe(inputs.merchantId!, inputs.resourceId!);
        actions.handlePostable(true);
      })
      .catch((_err: Error) => {
        actions.handlePostable(true);
      });
  }

  public async componentDidMount() {
    const { merchantResource, settings } = this.props;
    const reservationId = settings.createUserWithResv!.reservationId;

    if (isProduction) {
      // Fire GTM booking_completion event
      gtag.bookingEvent({
        step: 'completion',
        reservationPublicId: reservationId,
        resourcePublicId: merchantResource.publicId,
      });
    }
  }

  public render() {
    const {
      intl,
      merchantResource,
      settings,
      postable,
      withExpressFlow,
    } = this.props;
    const reservations = this.props.inputs;
    const reservationId = settings.createUserWithResv!.reservationId;

    return (
      <div>
        {settings.finishedStatus === 'accepted' && <BooksCompleteAlert />}
        {settings.finishedStatus === 'pending' && <BooksRequestAlert />}
        <BooksSection>
          <UserContextConsumer>
            {context =>
              settings.createUserWithResv!.available &&
              (this.state.submitted ? (
                <BooksCenteringUnderLineMessage>
                  {intl.formatMessage({
                    id: 'books.formsLabel.createUserSubmitted',
                  })}
                </BooksCenteringUnderLineMessage>
              ) : (
                <InputCompletionCreateUser
                  postable={postable}
                  reservationId={reservationId}
                  errors={context.auth.errors}
                  email={reservations.userInfo!.customerFields!.email}
                  onSubmit={(query: CreateUserWithResvQuery) =>
                    this.handleCreateUser(query, context)
                  }
                />
              ))
            }
          </UserContextConsumer>
        </BooksSection>
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.booksItems',
          })}
          paddingTop={true}
        >
          {reservations.course && (
            <BooksMenuItemList
              nickname={this.props.publicId}
              resourceId={merchantResource.publicId}
              resourceName={merchantResource.name}
              resourceImage={
                merchantResource.primaryImage
                  ? merchantResource.primaryImage.thumb &&
                    merchantResource.primaryImage.thumb.url
                  : ''
              }
              resourceUrl={`/${this.props.publicId}/${merchantResource.publicId}`}
              options={reservations.options || []}
              name={reservations.course ? reservations.course.name : ''}
              price={
                reservations.payment &&
                reservations.payment.paymentMethod.method !== 'subscription' &&
                reservations.payment.paymentMethod.method !== 'ticket'
                  ? reservations.payment.paymentMethod.price
                  : undefined
              }
              payment={
                reservations.payment &&
                reservations.payment.paymentMethod.method
              }
              staff={
                reservations.staff && !$.isRandomStaff(reservations.staff)
                  ? reservations.staff.name
                  : ''
              }
              courseDate={
                reservations.timeSlotAvailability
                  ? reservations.timeSlotAvailability.date
                  : ''
              }
              courseTime={
                reservations.timeSlotAvailability
                  ? reservations.timeSlotAvailability.value
                  : ''
              }
            />
          )}
        </BooksLabeledContainer>
        {!withExpressFlow && (
          <BooksSection withPadding={true} rightSide={true}>
            <Button
              isGhost={true}
              isFull={true}
              buttonSize={ButtonSize.Medium}
              href={`/${this.props.publicId}`}
            >
              {intl.formatMessage({
                id: 'books.formsLabel.backToMainPage',
              })}
            </Button>
          </BooksSection>
        )}
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.attentionForBooking',
          })}
          contentsPadding={true}
          paddingTop={true}
        >
          <BooksNotes notes={$.createNotes(intl, settings)} />
        </BooksLabeledContainer>
      </div>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooksInputs,
}: {
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsResourcesBooksInputs,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    handleSetMe: (merchantId: string, resourceId: string) =>
      setMeToBookInfo(merchantId, resourceId)(dispatch),
    handlePostable: (postable: boolean) => setPostable(postable)(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(scrollToTop(InputCompletion)));
